import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import infraChangeRequestService from "../../services/infraChangeRequest";
import { useSelector } from "react-redux";
import {
	Form,
	Input,
	Select,
	Button,
	Row,
	Col,
	Card,
	notification,
	Upload,
	Spin,
	DatePicker,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import LocationComponent from "../../components/location";

// FOR CUSTOM FIELDS
import customFieldService from "../../services/customField";
import commonCrud from "../../services/commonCrud";
import dayjs from "dayjs";
import RenderCustomFields from "../../components/renderCustomFields";

import DepartmentComponent from "../../components/department";
import DesignationComponent from "../../components/designation";
import SectionComponent from "../../components/section";
import UserComponent from "../../components/user";

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

export default function IcrForm() {
	const [form] = Form.useForm();
	const affectedComponentsWatch = Form.useWatch("affected_components", form);
	const navigate = useNavigate();

	const [edit, setEdit] = useState(null);
	const [loading, setLoading] = useState(false);
	const [Loading, setMdmLoading] = useState(false);
	const [fileList, setFileList] = useState([]);
	const [isDraft, setIsDraft] = useState(null);
	const [deletedAttachments, setDeletedAttachments] = useState([]);

	const { currentUser } = useSelector((state) => state.user);
	const defaultLocationId = currentUser ? currentUser.location.id : null;
	const defaultDepartmentId = currentUser ? currentUser.department.id : null;

	const { id } = useParams();

	// FOR CUSTOM FIELDS
	const [customfields, setCustomFields] = useState([]);
	const [dynamicOptions, setDynamicOptions] = useState({});

	// Custom fields
	const getCustomFields = async () => {
		await customFieldService.getcustomFields(3).then((response) => {
			const sorted = [...response].sort((a, b) => a.sort - b.sort);
			setCustomFields(sorted);
		});
	};

	const fetchDynamicOptions = async (
		reference,
		dataType = "dynamic",
		sortOption = "none",
	) => {
		if (!reference) return;
		if (dynamicOptions[reference]) return; // already cached

		try {
			if (dataType === "lookups") {
				const response = await commonCrud.lookups(reference);
				if (response && Array.isArray(response)) {
					let options = response.map((val) => ({ label: val, value: val }));
					if (sortOption === "asc")
						options.sort((a, b) => a.label.localeCompare(b.label));
					else if (sortOption === "desc")
						options.sort((a, b) => b.label.localeCompare(a.label));
					setDynamicOptions((prev) => ({ ...prev, [reference]: options }));
				}
				return;
			}

			const apiMap = {
				departments: commonCrud.departments,
				cost_centers: commonCrud.cost_centers,
				users: () => commonCrud.users("all"),
				projects: commonCrud.projects,
				sections: commonCrud.sections,
				locations: commonCrud.locations,
				equipments: commonCrud.equipments,
				forms: commonCrud.forms,
				subscribers: commonCrud.subscribers,
				approvers: () => commonCrud.approvers(1, 100),
				makes: () => commonCrud.makes(1, 100),
				companies: () => commonCrud.companies(1, 100),
				services: () => commonCrud.services(1, 100),
			};

			const fetchFn = apiMap[reference];
			if (!fetchFn) return;

			const response = await fetchFn();
			if (response && Array.isArray(response)) {
				let options = response.map((item) => ({
					label: item.name || item.title || item.cost_center || item.id,
					value: item.id,
				}));
				if (sortOption === "asc")
					options.sort((a, b) => a.label.localeCompare(b.label));
				else if (sortOption === "desc")
					options.sort((a, b) => b.label.localeCompare(a.label));
				setDynamicOptions((prev) => ({ ...prev, [reference]: options }));
			}
		} catch (error) {
			console.error(`Failed to fetch ${reference} options:`, error);
		}
	};

	useEffect(() => {
		if (customfields.length > 0) {
			customfields.forEach((f) => {
				if (
					(f.data_type === "dynamic" || f.data_type === "lookups") &&
					f.data_reference
				) {
					fetchDynamicOptions(f.data_reference, f.data_type, f.sort_options);
				}
			});
		}
	}, [customfields]);

	useEffect(() => {
		getCustomFields();
		if (id) {
			getRecordDetails(id);
		}
	}, [id]);

	const uploadProps = {
		onRemove: (file) => {
			const newFileList = fileList.filter((f) => f.uid !== file.uid);
			setFileList(newFileList);
			if (file.url) {
				setDeletedAttachments((prev) => [...prev, file.key]);
			}
		},
		beforeUpload: (file) => {
			setFileList((prev) => [...prev, file]);
			return false;
		},
		fileList,
	};

	const getRecordDetails = async (recordId) => {
		setMdmLoading(true);
		try {
			const record = await infraChangeRequestService.getById(recordId);
			setEdit(record);

			setFileList(
				(record.attachments || []).map((attachment) => ({
					key: attachment.id,
					uid: attachment.id,
					name: attachment.original_title,
					status: "done",
					url: attachment.filename,
				})),
			);

			let affected_components = record.affected_components || [];
			if (typeof affected_components === "string") {
				try {
					affected_components = JSON.parse(affected_components);
				} catch (e) {
					affected_components = [];
				}
			}

			const standardComponents = [
				"Operating System",
				"Patch",
				"Network Device",
				"Physical & Environment Controllers",
				"Others",
			];

			let mappedComponents = [];
			let otherComponentValue = "";

			affected_components.forEach((comp) => {
				if (standardComponents.includes(comp)) {
					mappedComponents.push(comp);
				} else {
					mappedComponents.push("Others");
					otherComponentValue = comp;
				}
			});
			mappedComponents = [...new Set(mappedComponents)];

			form.setFieldsValue({
				request_title: record.request_title,
				requestor_id: record.requestor_id?.id,
				location_id: record.location?.id,
				department_id: record.department?.id,
				section_id: record.section?.id,
				affected_components: mappedComponents,
				other_affected_component: otherComponentValue,
				change_request_specification: record.change_request_specification,
				old_values: record.old_values ? record.old_values : "",
				proposed_new_values: record.proposed_new_values
					? record.proposed_new_values
					: "",
				change_tested: record.change_tested,
				roll_back_scenario: record.roll_back_scenario,
				request_live_and_functional: record.request_live_and_functional,
				required_date: record.required_date
					? dayjs(record.required_date)
					: null,
			});

			// FOR CUSTOM FIELDS
			if (record.custom_fields && record.custom_fields.length > 0) {
				const customFieldValues = {};
				record.custom_fields.forEach((item) => {
					const fieldKey = `field_${item.custom_field.id}`;
					const label = item.custom_field.label?.toLowerCase() || "";
					const type = item.custom_field.type;

					if (
						type === "Date" ||
						type === "Datetime" ||
						label.includes("date") ||
						label.includes("dob") ||
						label.includes("day")
					) {
						const parsedDate = dayjs(item.value);
						customFieldValues[fieldKey] =
							dayjs.isDayjs(parsedDate) && parsedDate.isValid()
								? parsedDate
								: null;
					} else if (type === "Multi") {
						customFieldValues[fieldKey] = item.value
							? item.value.split(",").map((v) => v.trim())
							: [];
					} else {
						customFieldValues[fieldKey] = item.value;
					}
				});
				form.setFieldsValue(customFieldValues);
			}
		} catch (error) {
			console.error("Failed to fetch record:", error);
			notification.error({
				message: "Error",
				description: "Failed to load record details.",
			});
		} finally {
			setMdmLoading(false);
		}
	};

	const handleSuccess = (msg, description) => {
		setLoading(false);
		notification.success({ message: msg, description });
		navigate("/dashboard");
	};

	const onFinish = async (values) => {
		setLoading(true);

		try {
			const formData = new FormData();

			let valuesCopy = { ...values };

			// Process other_affected_component
			if (
				(valuesCopy.affected_components || []).includes("Others") &&
				valuesCopy.other_affected_component
			) {
				valuesCopy.affected_components = valuesCopy.affected_components.filter(
					(c) => c !== "Others",
				);
				valuesCopy.affected_components.push(
					valuesCopy.other_affected_component,
				);
			}
			delete valuesCopy.other_affected_component;

			// Append all standard fields
			for (const key in valuesCopy) {
				if (key.startsWith("field_")) continue; // handled separately

				const val = valuesCopy[key];
				if (val === undefined || val === null) {
					if (key === "old_values" || key === "proposed_new_values") {
						formData.append(key, "");
					}
					continue;
				}

				if (Array.isArray(val)) {
					val.forEach((item, index) => {
						formData.append(`${key}[${index}]`, item ?? "");
					});
				} else if (dayjs.isDayjs(val)) {
					formData.append(key, val.format("YYYY-MM-DD"));
				} else {
					formData.append(key, val);
				}
			}

			// HANDLE CUSTOM FIELDS
			Object.keys(values).forEach((key) => {
				if (key.startsWith("field_")) {
					const fieldId = key.replace("field_", "");
					formData.append(
						`custom_fields[${fieldId}]`,
						Array.isArray(values[key]) ? values[key].join(",") : values[key],
					);
				}
			});

			// Attachments
			fileList.forEach((file, index) => {
				if (!file.url) {
					formData.append(`attachments[${index}]`, file);
				}
			});

			deletedAttachments.forEach((attachmentId, index) => {
				formData.append(`deleted_attachments[${index}]`, attachmentId);
			});

			formData.append("save_as_draft", isDraft ? "true" : "false");
			formData.append("form_id", 10);

			let response;
			if (edit && edit.id) {
				response = await infraChangeRequestService.update(edit.id, formData);
			} else {
				response = await infraChangeRequestService.store(formData);
			}

			if (response.success) {
				handleSuccess(
					edit ? "Record Updated" : "Infrastructure Change Request Submitted",
					response.message,
				);
			}
		} catch (error) {
			setLoading(false);
			notification.error({
				message: "Error",
				description: error?.response?.data?.message || "Something went wrong.",
			});
		}
	};

	return (
		<DefaultLayout>
			<Spin spinning={Loading}>
				<Form
					form={form}
					onFinish={onFinish}
					layout='vertical'
					className='mb-50'
				>
					<Card title='Basic Details'>
						<Row gutter={16}>
							{/* Request Title */}
							<Col span={24}>
								<Form.Item
									name='request_title'
									label='Request Title'
									rules={[
										{ required: true, message: "Please enter a request title" },
									]}
								>
									<Input placeholder='Enter request title' />
								</Form.Item>
							</Col>

							{/* Requestor Name */}
							<Col span={12}>
								<Form.Item
									name='requestor_id'
									label='Requestor Name'
									rules={[
										{ required: true, message: "Please select a requestor" },
									]}
								>
									<UserComponent multiselect={false} forMR={true} />
								</Form.Item>
							</Col>

							{/* Location */}
							<Col span={12}>
								<Form.Item
									name='location_id'
									label='Location'
									initialValue={defaultLocationId}
									rules={[
										{ required: true, message: "Please select a location" },
									]}
								>
									<LocationComponent all={true} />
								</Form.Item>
							</Col>

							{/* Department */}
							<Col span={12}>
								<Form.Item
									name='department_id'
									label='Department'
									initialValue={defaultDepartmentId}
									rules={[
										{ required: true, message: "Please select a department" },
									]}
								>
									<DepartmentComponent all={true} />
								</Form.Item>
							</Col>
						</Row>
					</Card>

					<Card title='Change Request Details' style={{ marginTop: 16 }}>
						<Row gutter={16}>
							{/* Component Affected */}
							<Col span={24}>
								<Form.Item
									name='affected_components'
									label='Component Affected'
									rules={[
										{
											required: true,
											message: "Please select at least one component",
										},
									]}
								>
									<Select
										mode='multiple'
										placeholder='Select components'
										allowClear
									>
										<Option value='Operating System'>Operating System</Option>
										<Option value='Patch'>Patch</Option>
										<Option value='Network Device'>Network Device</Option>
										<Option value='Physical & Environment Controllers'>
											Physical &amp; Environment Controllers
										</Option>
										<Option value='Others'>Others</Option>
									</Select>
								</Form.Item>
							</Col>

							{/* Other Component Affected */}
							{(affectedComponentsWatch || []).includes("Others") && (
								<Col span={24}>
									<Form.Item
										name='other_affected_component'
										label='Other Component Name'
										rules={[
											{
												required: true,
												message: "Please specify the other component",
											},
										]}
									>
										<Input placeholder='Enter other component name' />
									</Form.Item>
								</Col>
							)}

							{/* Change Request Specification & Justification */}
							<Col span={24}>
								<Form.Item
									name='change_request_specification'
									label='Change Request Specification & Justification'
									rules={[
										{
											required: true,
											message: "Please provide specification & justification",
										},
									]}
								>
									<TextArea
										rows={4}
										placeholder='Describe the change specification and justification'
									/>
								</Form.Item>
							</Col>

							{/* Old Value / State */}
							<Col span={12}>
								<Form.Item name='old_values' label='Old Value / State'>
									<TextArea
										rows={3}
										placeholder='Describe the current/old state'
									/>
								</Form.Item>
							</Col>

							{/* Proposed New Value / State */}
							<Col span={12}>
								<Form.Item
									name='proposed_new_values'
									label='Proposed New Value / State'
								>
									<TextArea
										rows={3}
										placeholder='Describe the proposed new state'
									/>
								</Form.Item>
							</Col>

							{/* Change Tested */}
							<Col span={12}>
								<Form.Item
									name='change_tested'
									label='Change Tested'
									rules={[
										{ required: true, message: "Please select an option" },
									]}
								>
									<Select placeholder='Select'>
										<Option value='Yes'>Yes</Option>
										<Option value='No'>No</Option>
										<Option value='N/A'>N/A</Option>
									</Select>
								</Form.Item>
							</Col>

							{/* Rollback Scenario */}
							<Col span={12}>
								<Form.Item
									name='roll_back_scenario'
									label='Rollback Scenario'
									rules={[
										{
											required: true,
											message: "Please select a rollback scenario",
										},
									]}
								>
									<Select placeholder='Select rollback scenario'>
										<Option value='Configuration Rollback'>
											Configuration Rollback
										</Option>
										<Option value='Configuration Backup Taken'>
											Configuration Backup Taken
										</Option>
										<Option value='No Rollback Required'>
											No Rollback Required
										</Option>
										<Option value='N/A'>N/A</Option>
									</Select>
								</Form.Item>
							</Col>

							{/* Request Live and Functional */}
							<Col span={12}>
								<Form.Item
									name='request_live_and_functional'
									label='Request Live and Functional'
									rules={[
										{ required: true, message: "Please select an option" },
									]}
								>
									<Select placeholder='Select'>
										<Option value='YES'>YES</Option>
										<Option value='NO'>NO</Option>
									</Select>
								</Form.Item>
							</Col>

							{/* Required Date */}
							<Col span={12}>
								<Form.Item
									name='required_date'
									label='Required Date'
									rules={[
										{
											required: true,
											message: "Please select a required date",
										},
									]}
								>
									<DatePicker style={{ width: "100%" }} />
								</Form.Item>
							</Col>
						</Row>
					</Card>

					{/* Attachments */}
					<Card title='Attachments' style={{ marginTop: 16 }}>
						<Dragger {...uploadProps}>
							<p className='ant-upload-drag-icon'>
								<InboxOutlined />
							</p>
							<p className='ant-upload-text'>
								Click or drag files to this area to upload
							</p>
							<p className='ant-upload-hint'>
								Support for single or bulk upload.
							</p>
						</Dragger>
					</Card>

					{/* Render Custom Fields */}
					<RenderCustomFields
						customfields={customfields}
						dynamicOptions={dynamicOptions}
						fetchDynamicOptions={fetchDynamicOptions}
					/>

					<div
						style={{ display: "flex", justifyContent: "space-between" }}
						className='mt-24 formbtnarea'
					>
						<Button
							type='primary'
							block
							disabled={loading}
							loading={loading}
							className='mr-20'
							onClick={() => {
								setIsDraft(false);
								form.submit();
							}}
						>
							Save &amp; Publish
						</Button>

						<Button
							loading={loading}
							disabled={loading}
							type='default'
							onClick={() => {
								setIsDraft(true);
								form.submit();
							}}
						>
							Save Draft
						</Button>
					</div>
				</Form>
			</Spin>
		</DefaultLayout>
	);
}
